import { FormattedMessage } from 'react-intl';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Typography } from '@/components/ui/typography';

import type { IneligibleOpening } from '@/components/pages/job-posting/types';

interface PostingUnavailableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ineligibleOpenings: IneligibleOpening[];
}

/**
 * Dialog shown when a user tries to post ineligible openings to job boards.
 * When count is 1: shows message based on reason (inactive vs sourcing_disabled).
 * When count > 1: shows generic message.
 */
export function PostingUnavailableDialog({
  open,
  onOpenChange,
  ineligibleOpenings,
}: PostingUnavailableDialogProps) {
  const count = ineligibleOpenings.length;
  const isSingle = count === 1;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="!w-[320px]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="Can't Post to Job Boards"
                description="Blocking dialog when user selection cannot be posted to boards"
              />
            </Typography>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Typography variant="subtitle1">
              {isSingle ? (
                <FormattedMessage
                  defaultMessage="This opening isn't eligible for posting. Check that it's active and sourcing is enabled."
                  description="Single-opening case: why post was blocked and how to fix in Hire"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="None of the selected openings are eligible for posting. Check that each is active and sourcing is enabled."
                  description="Multi-opening case: all selected jobs failed eligibility checks"
                />
              )}
            </Typography>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <FormattedMessage
              defaultMessage="Close"
              description="Close the dialog"
            />
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
